import { getColorv2 } from '@local/finsera-core/src/helpers';
import { _, am5, am5percent } from '@local/finsera-core/src/libs';
import * as types from '@local/finsera-core/src/types';

import { FONT_FAMILY, FONT_SIZE } from '../../../../theme/layout-size';

const EMPTY_ARRAY: any[] = [];

export const createSeries = ({
  root,
  chart,
  data,
  category,
  tooltipText,
  doughnut,
  hideSeries = EMPTY_ARRAY,
  onSelect,
  hideLabelsOnActive,
  truncateLabels,
}: {
  root: am5.Root;
  chart: am5percent.PieChart;
  data: types.widgets.TableData;
  category: string;
  tooltipText: string;
  hideSeries: string[];
  doughnut: boolean;
  seriesOrder: (_c: string[]) => string[];
  onSelect: (_category: string) => void;
  hideLabelsOnActive: boolean;
  truncateLabels: number | undefined;
}) => {
  const series = chart.series.push(
    am5percent.PieSeries.new(root, {
      valueField: 'value',
      categoryField: 'category',
      endAngle: 270,
      innerRadius: doughnut ? am5.percent(50) : undefined,
    })
  );

  series.slices.template.setAll({
    strokeWidth: 2,
    stroke: am5.color(0xffffff),
  });

  series.slices.template.adapters.add('fill', (fill, target) => {
    const dataContext = target.dataItem?.dataContext as { category: string };
    const index = transformedData.findIndex((item) => item.category === dataContext?.category);
    return am5.color(getColorv2(index));
  });

  series.labels.template.setAll({
    text: '{category}',
    radius: 30,
    ...(truncateLabels && {
      maxWidth: truncateLabels,
      oversizedBehavior: 'truncate',
      breakWords: false,
      ellipsis: '...',
    }),
  });

  (series.labels.template as any).setAll({
    fontSize: FONT_SIZE.xxs,
    fontWeight: 500,
    fontFamily: FONT_FAMILY,
    fill: am5.color(0x000000),
  });

  // Set up tooltips
  const tooltip = series.set('tooltip', am5.Tooltip.new(root, {}));
  tooltip.label.setAll({
    fontSize: FONT_SIZE.xxs,
    fontFamily: FONT_FAMILY,
  });

  series.slices.template.setAll({
    tooltipText,
  });

  let currentSlice: am5.Slice | null = null;

  const handleSliceActivation = (active: boolean, slice: am5.Slice) => {
    if (currentSlice && currentSlice != slice && active) {
      currentSlice.set('active', false);
    }

    if (active) {
      onSelect(slice.dataItem.get('category' as any) as string);
      if (hideLabelsOnActive) {
        series.labels.template.set('forceHidden', true);
        series.ticks.template.set('forceHidden', true);
      }
    } else {
      onSelect(null);
      if (hideLabelsOnActive) {
        series.labels.template.set('forceHidden', false);
        series.ticks.template.set('forceHidden', false);
      }
    }

    currentSlice = active ? slice : null;
  };

  // Hide labels for small slices
  if (data.length > 5) {
    series.labels.template.adapters.add('y', function (y, target) {
      const dataItem = target.dataItem;
      if (dataItem) {
        const tick = dataItem.get('tick' as any);
        if (tick) {
          if (dataItem.get('valuePercentTotal' as any) <= 4) {
            target.set('forceHidden', true);
            tick.set('forceHidden', true);
          } else {
            target.set('forceHidden', false);
            tick.set('forceHidden', false);
          }
        }
        return y;
      }
    });
  }

  series.slices.template.on('active', function (active, slice) {
    handleSliceActivation(active, slice);
  });

  // Function to deselect the active slice externally
  const deselectActiveSlice = () => {
    if (currentSlice) {
      currentSlice.set('active', false);
      handleSliceActivation(false, currentSlice);
    }
  };

  // Expose the deselect function
  (chart as any).deselectActiveSlice = deselectActiveSlice;

  // Transform data for pie chart
  const firstObj = data?.[0] || {};
  const seriesKeys = Object.keys(_.omit(firstObj, [category])).filter((el) => el);
  const transformedData = data
    .map((item, index) => {
      const value = seriesKeys.reduce((sum, key) => sum + (Number(item[key]) || 0), 0);
      return {
        category: item[category],
        value,
        sliceSettings: {
          fill: am5.color(getColorv2(index)),
        },
      };
    })
    .filter((item) => !hideSeries.includes(item.category as any));

  // Set data
  series.data.setAll(transformedData);

  // // Pre-select first slice
  // series.events.on('datavalidated', function () {
  //   series.slices.getIndex(0).set('active', true);
  // });
};
